/**
 * Composant affichant les KPIs de paiement d'un chantier
 * 3 cartes : Devis HT, Encaissé, Reste
 */

import { Card, CardContent } from '@/components/ui/card';
import { formatMontant } from '@/lib/formatters';
import type { PaiementTotals } from '@/types/paiement';


interface PaiementKPIsProps {
  totals: PaiementTotals | null;
  montantDevisHT?: number; // Fallback si totals pas encore chargé
  isLoading?: boolean;
}

export function PaiementKPIs({
  totals,
  montantDevisHT = 0,
  isLoading = false
}: PaiementKPIsProps) {
  // Utiliser les totaux calculés ou les valeurs de fallback
  const devisHT = totals?.total_a_encaisser ?? montantDevisHT;
  const encaisse = totals?.total_encaisse ?? 0;
  const reste = totals?.reste_a_encaisser ?? (devisHT - encaisse);
  const pourcentage = totals?.pourcentage_encaisse ?? (devisHT > 0 ? Math.round((encaisse / devisHT) * 100) : 0);

  // Skeleton pendant le chargement
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-20 mb-2" />
                <div className="h-8 bg-muted rounded w-32" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Devis HT / Total à encaisser */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground mb-1">
            {totals?.total_ajustements !== 0 ? 'Total à encaisser' : 'Devis HT'}
          </p>
          <p className="text-2xl font-bold text-foreground">
            {formatMontant(devisHT)}
          </p>
          {/* Si ajustements, montrer le détail */}
          {totals && totals.total_ajustements !== 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Devis: {formatMontant(totals.total_devis)}
              {totals.total_ajustements > 0
                ? ` + ${formatMontant(totals.total_ajustements)}`
                : ` - ${formatMontant(Math.abs(totals.total_ajustements))}`
              }
            </p>
          )}
        </CardContent>
      </Card>

      {/* Encaissé */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Encaissé</p>
          <p className={`text-2xl font-bold ${encaisse > 0 ? 'text-green-500' : 'text-foreground'}`}>
            {formatMontant(encaisse)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            ({pourcentage}%)
          </p>
        </CardContent>
      </Card>

      {/* Reste à encaisser */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Reste</p>
          <p className={`text-2xl font-bold ${reste === 0 ? 'text-green-500' : reste > 0 ? 'text-orange-500' : 'text-foreground'}`}>
            {formatMontant(reste)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default PaiementKPIs;
